import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';

import { ApplicationContext } from '~/shared/context/context';
import type { Competency } from '~/shared/data-contract/job-profile';
import { getCompetencyTitle } from '~/shared/data-contract/job-profile';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconSearch } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useCompetencies } from '../../api/use-competencies';
import { useTrackSearch } from '../../hooks/analytics/use-track-search';
import type { ProfileForm } from '../types';

const SearchWrapper = styled.div({
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${theme.colors.gray[400]}`,
    display: 'flex',
    gap: theme.spacing.s,
    padding: theme.spacing.s,
    position: 'relative',
});

const StyledInput = styled(Input)({
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.gray[700],
    flex: 1,
    ...theme.typography.body.S.regular,
    padding: 0,
    paddingLeft: theme.spacing.s,
});

const StyledSearchIcon = styled('label')({
    alignItems: 'center',
    color: theme.colors.gray[700],
    display: 'flex',
    opacity: 0.5,
});

const List = styled.div({
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column' as const,
    gap: '4px',
    maxHeight: '25vh',
});

const Empty = styled.div({
    color: theme.colors.gray[500],
    fontSize: theme.typography.body.S.regular.fontSize,
    padding: `${theme.spacing.m} ${theme.spacing.m}`,
    textAlign: 'center',
});

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.s,
    minWidth: '240px',
    overflow: 'hidden',
});

type UseCompetencySearchParams = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly debounceDelay: number;
    readonly onCompetencyChange?: () => void;
};

type UseCompetencySearchResult = {
    readonly availableCompetencies: readonly Competency[];
    readonly handleCompetencySelect: (competencyId: string) => void;
    readonly isCompetencySelected: (competencyId: string) => boolean;
    readonly isLoading: boolean;
    readonly searchTerm: string;
    readonly setSearchTerm: (value: string) => void;
};

const useCompetencySearch = ({
    architectureId,
    control,
    debounceDelay,
    onCompetencyChange,
}: UseCompetencySearchParams): UseCompetencySearchResult => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, debounceDelay, { leading: true });
    const { applicationState } = useContext(ApplicationContext);
    const { data: competencies, isLoading } = useCompetencies();
    const { trackSearchExecuted } = useTrackSearch({ architectureId });

    const { fields, insert } = useFieldArray({
        control,
        keyName: '_id',
        name: 'competencies',
    });

    const competenciesArray = is.undefined(competencies) ? [] : [...competencies.values()];

    const filteredCompetencies = competenciesArray.filter((competency) => {
        const title = getCompetencyTitle(competency, applicationState.language.code);

        return title.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const availableCompetencies = filteredCompetencies.filter(
        (competency) => !fields.some((field) => field.id === competency.id),
    );

    const debouncedResultCount = competenciesArray.filter((competency) => {
        const title = getCompetencyTitle(competency, applicationState.language.code);

        return title.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    }).length;

    useEffect(() => {
        if (!is.nonEmptyString(debouncedSearchTerm)) {
            return;
        }

        trackSearchExecuted({
            query: debouncedSearchTerm,
            resultCount: debouncedResultCount,
            scope: 'competencies',
        });
    }, [debouncedSearchTerm, debouncedResultCount, trackSearchExecuted]);

    const handleCompetencySelect = (competencyId: string): void => {
        const competency = competenciesArray.find((c) => c.id === competencyId);
        if (is.nullOrUndefined(competency)) {
            return;
        }

        const alreadyExists = fields.some((field) => field.id === competency.id);
        if (!alreadyExists) {
            insert(1, {
                ...competency,
                skills: [],
            });
            onCompetencyChange?.();
        }

        setSearchTerm('');
    };

    const isCompetencySelected = (competencyId: string): boolean => {
        return fields.some((field) => field.id === competencyId);
    };

    return {
        availableCompetencies,
        handleCompetencySelect,
        isCompetencySelected,
        isLoading,
        searchTerm,
        setSearchTerm,
    };
};

type CompetencySearchContentProps = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly debounceDelay?: number;
    readonly onCompetencyChange?: () => void;
};

export const CompetencySearchContent: FunctionComponent<CompetencySearchContentProps> = ({
    architectureId,
    control,
    debounceDelay = 500,
    onCompetencyChange,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { applicationState, translate } = useContext(ApplicationContext);
    const {
        availableCompetencies,
        handleCompetencySelect,
        isCompetencySelected,
        isLoading,
        searchTerm,
        setSearchTerm,
    } = useCompetencySearch({
        architectureId,
        control,
        debounceDelay,
        onCompetencyChange,
    });

    useEffect(() => {
        // we do this so we auto focus on the input after the dropdown is opened
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    }, []);

    return (
        <Wrapper>
            <SearchWrapper>
                <StyledSearchIcon htmlFor="competency-search-input">
                    <IconSearch size={12} />
                </StyledSearchIcon>
                <StyledInput
                    ref={inputRef}
                    autoFocus={true}
                    id="competency-search-input"
                    placeholder={translate('pages.job-architecture-v2.job-profile-competency-search-placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                />
            </SearchWrapper>
            <ScrollArea orientation="vertical">
                <List>
                    {is.emptyArray(availableCompetencies) && !isLoading && (
                        <Empty>{translate('validation.search-term')}</Empty>
                    )}
                    {isLoading && (
                        <Fragment>
                            <DropdownMenuItem>
                                <Skeleton height="10px" width="100%" />
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <Skeleton height="10px" width="90%" />
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <Skeleton height="10px" width="80%" />
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <Skeleton height="10px" width="90%" />
                            </DropdownMenuItem>
                        </Fragment>
                    )}
                    {availableCompetencies.map((competency) => (
                        <DropdownMenuItem
                            key={competency.id}
                            disabled={isCompetencySelected(competency.id)}
                            onSelect={() => handleCompetencySelect(competency.id)}
                        >
                            {getCompetencyTitle(competency, applicationState.language.code)}
                        </DropdownMenuItem>
                    ))}
                </List>
            </ScrollArea>
        </Wrapper>
    );
};
